import { useCallback, useEffect, useRef, useState } from 'react';
import { StudentService, StudentTrainingService } from '../services';
import type { StudentTrainingListItem } from '../services/StudentTrainingService';

interface UseStudentAssignedTrainingsResult {
  assignedTrainings: StudentTrainingListItem[];
  studentName: string;
  loading: boolean;
  error: string | null;
  refreshAssignedTrainings: () => void;
}

export const useStudentAssignedTrainings = (studentId: string): UseStudentAssignedTrainingsResult => {
  const [assignedTrainings, setAssignedTrainings] = useState<StudentTrainingListItem[]>([]);
  const [studentName, setStudentName] = useState('');
  const [loading, setLoading] = useState(Boolean(studentId));
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const requestIdRef = useRef(0);

  const refreshAssignedTrainings = useCallback(() => {
    setReloadKey((currentKey) => currentKey + 1);
  }, []);

  useEffect(() => {
    if (!studentId) {
      setAssignedTrainings([]);
      setStudentName('');
      setLoading(false);
      setError(null);
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    let isMounted = true;

    const load = async () => {
      setLoading(true);

      try {
        const [assigned, student] = await Promise.all([
          StudentTrainingService.list(studentId),
          StudentService.findById(studentId).catch(() => null),
        ]);

        if (!isMounted || currentRequestId !== requestIdRef.current) {
          return;
        }

        setAssignedTrainings(assigned);
        setStudentName(student?.full_name?.trim() ?? '');
        setError(null);
      } catch (serviceError) {
        if (!isMounted || currentRequestId !== requestIdRef.current) {
          return;
        }

        setAssignedTrainings([]);
        setError(serviceError instanceof Error
          ? serviceError.message
          : 'Não foi possível carregar os treinos da aluna.');
      } finally {
        if (isMounted && currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      isMounted = false;
    };
  }, [reloadKey, studentId]);

  return {
    assignedTrainings,
    studentName,
    loading,
    error,
    refreshAssignedTrainings,
  };
};
